import React from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from '../../../../../../../../store';
import LoadingDataOverlay from '../../../../../../../../components/global/LoadingDataOverlay/LoadingDataOverlay';
import RedistributionImportItemsTable from './RedistributionImportItemsTable';
import RedistributionImportSummary from './RedistributionImportSummary';
import { Button, Column } from '@ydistri/ds';
import RedistributionImportItemsActions from './RedistributionImportItemsActions';
import { Alert } from 'antd';
import { useTemplateOrCalculation } from '../../../../../../../../hooks/useTemplateOrCalculation';
import { useGetCalculationRecordImportSummaryQuery } from '../../../apiCalculationRedistributionImport';
import { getRequestErrorMessage } from '../../../../../../../../lib/utils/getRequestErrorMessage';
import { RecordImportAction } from '@ydistri/api-sdk';

const RedistributionImportStepValidation: React.FC = () => {
  const currentStepRunning = useSelector(
    (state: ReduxState) => state.calculations.redistributionImport.currentStepRunning,
  );

  // to prevent api requests when import is deleted, but components displaying import items
  // are still mounted and initiate the request once cache is invalidated
  // set in useResetImport() hook
  const importPendingDelete = useSelector(
    (state: ReduxState) => state.calculations.redistributionImportPendingDelete,
  );
  const calculation = useTemplateOrCalculation();
  const summary = useGetCalculationRecordImportSummaryQuery(calculation.id, {
    skip: currentStepRunning || importPendingDelete,
  });
  const canFinalize =
    !summary.isFetching &&
    !summary.isError &&
    summary.data?.some(
      item =>
        item.itemCount > 0 &&
        (item.recordImportAction === RecordImportAction.Insert ||
          item.recordImportAction === RecordImportAction.CreateSkuAndInsert),
    );

  if (currentStepRunning) {
    return <LoadingDataOverlay active={true} title="Validating" />;
  }

  return (
    <Column $gap="8px" $flexGrow={1}>
      {!importPendingDelete && (
        <>
          {summary.isError && (
            <Alert
              type="error"
              showIcon
              message="Import cannot continue"
              description={getRequestErrorMessage(
                summary.error,
                'Validation could not be completed. Please try again.',
              )}
              action={
                <Button onClick={() => void summary.refetch()} disabled={summary.isFetching}>
                  Retry validation
                </Button>
              }
            />
          )}
          {!summary.isError && <RedistributionImportSummary />}
          <RedistributionImportItemsTable />
          <RedistributionImportItemsActions canFinalize={!!canFinalize} />
        </>
      )}
    </Column>
  );
};

export default RedistributionImportStepValidation;
